import apiClient from './api';
import { AnalysisResult } from './projectService';

export const reanalyzeResult = async (resultId: number, context?: string): Promise<AnalysisResult> => {
  const payload = context !== undefined ? { additional_context: context } : {};
  const response = await apiClient.post(`/resultados/${resultId}/re-analyze`, payload);
  return response.data;
};

export const getResultHistory = async (resultId: number): Promise<AnalysisResult[]> => {
  const response = await apiClient.get(`/resultados/${resultId}/history`);
  return response.data;
};

export const validateApplicability = async (resultId: number, applies: boolean): Promise<AnalysisResult> => {
  const response = await apiClient.post(`/resultados/${resultId}/validate-applicability`, { applies });
  return response.data;
};

export const assessRisk = async (resultId: number, probability: number, impact: number): Promise<AnalysisResult> => {
  const response = await apiClient.post(`/resultados/${resultId}/assess-risk`, {
    probability_score: probability,
    impact_score: impact,
  });
  return response.data;
};

export const bulkAssessRiskAI = async (projectId: string): Promise<{ message: string }> => {
  const response = await apiClient.post(`/resultados/proyectos/${projectId}/bulk-assess-risk-ai`);
  return response.data;
};

// New interfaces for statistics
export interface RiskDistributionItem {
  risk_level: string;
  count: number;
  percentage: number;
}

export interface RiskDistributionStats {
  total_assessed_items: number;
  risk_levels: RiskDistributionItem[];
}

export interface RiskMatrixCell {
  probability: number;
  impact: number;
  count: number;
  items: number[];
}

export interface RiskMatrixStats {
  total_assessed_items: number;
  matrix_cells: RiskMatrixCell[];
  max_cell_count: number;
}

export interface WorkflowProgressStats {
  total_items: number;
  pending_ai_analysis: number;
  pending_applicability_review: number;
  pending_risk_assessment: number;
  completed: number;
  failed: number;
  completion_percentage: number;
}

export interface ApplicabilityBreakdownStats {
  total_analyzed: number;
  applicable: number;
  not_applicable: number;
  pending_review: number;
  applicability_rate: number;
}

export interface ComprehensiveDashboardStats {
  workflow_progress: WorkflowProgressStats;
  applicability_breakdown: ApplicabilityBreakdownStats;
  risk_distribution?: RiskDistributionStats;
  risk_matrix?: RiskMatrixStats;
}

// New statistics API functions
export const getRiskDistribution = async (projectId: string): Promise<RiskDistributionStats> => {
  const { data } = await apiClient.get(`/resultados/proyectos/${projectId}/risk-distribution`);
  return data;
};

export const getRiskMatrix = async (projectId: string): Promise<RiskMatrixStats> => {
  const { data } = await apiClient.get(`/resultados/proyectos/${projectId}/risk-matrix`);
  return data;
};

export const getWorkflowProgress = async (projectId: string): Promise<WorkflowProgressStats> => {
  const { data } = await apiClient.get(`/resultados/proyectos/${projectId}/workflow-progress`);
  return data;
};

export const getApplicabilityBreakdown = async (projectId: string): Promise<ApplicabilityBreakdownStats> => {
  const { data } = await apiClient.get(`/resultados/proyectos/${projectId}/applicability-breakdown`);
  return data;
};

export const getComprehensiveDashboardStats = async (projectId: string): Promise<ComprehensiveDashboardStats> => {
  const { data } = await apiClient.get(`/resultados/proyectos/${projectId}/dashboard-stats`);
  return data;
}; 